import { formatAppDate } from 'common/hooks/utils';
import { columns as widgetResponseDetailsExtendedColumns } from '../WidgetResponseDetailsExtended';
import { columns as widgetResponseDetailsColumns } from '../WidgetResponseDetails';
import { columns as widgetAlignmentResponseDetailsColumns } from '../WidgetAlignmentResponseDetail';

import {
  initialColumns as widgetAlignmentProfileQuestionColumns,
  endColumns as widgetAlignmentProfileQuestionEndColumns,
  QuestionType,
} from '../WidgetAlignmentProfileQuestion';
import { columns as widgetAlignmentShiftColumns } from '../WidgetAlignmentShift';
import { columns as widgetMIRDetailsColumns } from '../WidgetMirDetails';
import { EXCEL_EXPORT_DATE_FORMAT } from 'common/constants';
import { ListResponse } from 'common/store/api/chart.api';
import { ExcelExportItem } from 'common/models/export.model';
import { GridCellProps } from '@progress/kendo-react-grid';
import { AlignmentTextCell } from '../widget-subcomponents/TreeList.subcomponents';
import { KwelloBarChartOptions, KwelloLineChartOptions } from 'common/models/chart.model';
import { QuestionModel } from 'common/models/dataset.model';
import { CommonFiltersItem, FilterType } from 'common/models/filter.model';
import { FILTER_ITEMS } from 'modules/insights/constants/constants-filter-configs';
import { AggregateType, GeographyRegionColor } from '../../../models/widget.model';
import { ColumnModel, KwelloGridColumnModel } from 'common/models/widget-config.model';
import { WidgetType } from 'common/models/widget.model';
import { ColumnOrderType, isNamedColumnOrder } from 'common/models/grid-config.model';
import {
  NotAvailableReason,
  PendingReason,
  SentimentLabel,
} from 'common/components/datagrid/Sentiment';

export const getCssColorHex = (cssVar: string): string => {
  const value = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  const hex = value;
  return hex;
};

// Call once on init
export const getCssColor = (cssVar: string): { r: number; g: number; b: number } => {
  const hex = getCssColorHex(cssVar);
  const bigint = parseInt(hex.replace('#', ''), 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255,
  };
};

/**
 * Converts a `QuestionModel` object into a `CommonFiltersItem` object
 * containing filter information for a dataset question.
 *
 * @param datasetQuestion - The `QuestionModel` object representing the dataset question.
 * @returns A `CommonFiltersItem` object with filter details for the survey and question.
 *
 * The returned object includes:
 * - `Survey`: An autocomplete filter with the survey's name and ID.
 * - `Question`: An autocomplete filter with the question's name and ID.
 */
export const convertDatasetQuestionToFilters = (
  datasetQuestion: QuestionModel
): CommonFiltersItem => {
  const filters = {
    Survey: {
      label: FILTER_ITEMS.category.label,
      type: FilterType.AUTOCOMPLETE,
      value: [
        {
          name: datasetQuestion.parent.name,
          value: datasetQuestion.parent.id.toString(),
        },
      ],
    },
    Question: {
      label: FILTER_ITEMS.question.label,
      type: FilterType.AUTOCOMPLETE,
      value: [
        {
          name: datasetQuestion.name,
          value: datasetQuestion.id.toString(),
        },
      ],
    },
  };

  return filters;
};

/**
 * Sorts an array of grid column properties based on a specified order of column indices.
 *
 * @param params - The parameters for sorting columns.
 * @param params.columns - An array of grid column properties to be sorted.
 * @param params.columnIndexOrder - Column order in either named format (PersistedColumnOrder[]) or legacy numeric format (number[]).
 * @returns A new array of grid column properties sorted according to the specified order.
 *          If the input columns or columnIndexOrder are empty or undefined, the original columns array is returned.
 */
export const sortColumns = ({
  columns,
  columnIndexOrder,
}: {
  columns: ColumnModel[] | KwelloGridColumnModel[];
  columnIndexOrder: ColumnOrderType | undefined;
}): ColumnModel[] | KwelloGridColumnModel[] => {
  if (!columns || columns.length === 0 || !columnIndexOrder || columnIndexOrder.length === 0) {
    return columns;
  }

  // Handle named column order format (new format)
  if (isNamedColumnOrder(columnIndexOrder)) {
    const newColumns = columnIndexOrder.reduce((acc: ColumnModel[], columnIndex) => {
      const column = columns.find(
        (column: any) =>
          column.field === columnIndex.id ||
          column.id === columnIndex.id ||
          (column?.title ?? column?.text) === columnIndex.id
      ) as ColumnModel;

      const hidden = columnIndex.hidden;

      if (!hidden && column) {
        acc.push(column);
      }

      return acc;
    }, []);

    return newColumns;
  }

  // Handle legacy numeric format (backward compatibility)
  return columns
    .map((column, index) => ({ ...column, orderIndex: columnIndexOrder[index] ?? index }))
    .sort((a: any, b: any) => a.orderIndex - b.orderIndex) as ColumnModel[];
};

/**
 * Generates a unique string by combining a random number with a base-36 encoding
 * and extracting a substring from it.
 */
export const uniqueString = (): string => crypto.randomUUID();

interface UpdateLineChartLocalStorageArgs {
  widgetId: string;
  options: KwelloLineChartOptions;
}

/**
 * Updates the local storage with the provided line chart options for a specific widget.
 *
 * @param {Object} params - The parameters for updating the local storage.
 * @param {string} params.widgetId - The unique identifier of the widget.
 * @param {any} params.options - The configuration options for the line chart to be stored.
 *
 * @returns {void}
 */

export const updateLineChartLocalStorage = ({
  widgetId,
  options,
}: UpdateLineChartLocalStorageArgs) => {
  localStorage.setItem(`kwelloLineChartOptions-${widgetId}`, JSON.stringify(options));
};

interface GetLineChartLocalStorageArgs {
  widgetId: string;
}

/**
 * Retrieves the line chart options from local storage for a specific widget.
 *
 * @param {Object} params - The parameters for retrieving local storage.
 * @param {string} params.widgetId - The unique identifier of the widget.
 * @returns {KwelloLineChartOptions | null} The line chart options stored in local storage.
 */

export const getLineChartLocalStorage = ({
  widgetId,
}: GetLineChartLocalStorageArgs): KwelloLineChartOptions | null => {
  const storedOptions = localStorage.getItem(`kwelloLineChartOptions-${widgetId}`);
  return storedOptions ? (JSON.parse(storedOptions) as KwelloLineChartOptions) : null;
};

interface UpdateBarChartLocalStorageArgs {
  widgetId: string;
  options: KwelloBarChartOptions;
}

/**
 * Updates the local storage with bar chart options for a specific widget.
 *
 * @param {Object} params - The parameters for updating local storage.
 * @param {string} params.widgetId - The unique identifier of the widget.
 * @param {any} params.options - The options to be stored for the bar chart.
 */
export const updateBarChartLocalStorage = ({
  widgetId,
  options,
}: UpdateBarChartLocalStorageArgs) => {
  localStorage.setItem(`kwelloBarChartOptions-${widgetId}`, JSON.stringify(options));
};

interface GetBarChartLocalStorageArgs {
  widgetId: string;
}

/**
 * Retrieves the bar chart options from local storage for a specific widget.
 *
 * @param {Object} params - The parameters for retrieving local storage.
 * @param {string} params.widgetId - The unique identifier of the widget.
 * @returns {KwelloBarChartOptions | null} The bar chart options stored in local storage.
 */

export const getBarChartLocalStorage = ({
  widgetId,
}: GetBarChartLocalStorageArgs): KwelloBarChartOptions | null => {
  const storedOptions = localStorage.getItem(`kwelloBarChartOptions-${widgetId}`);
  return storedOptions ? (JSON.parse(storedOptions) as KwelloBarChartOptions) : null;
};

// These should be Bryntum Cell Types, will contain field names for backwards compatibility
export const NON_EXPORT_FIELDS = ['attachment', 'volume', 'sparkline'];

export const GRID_COLUMNS: Record<string, ColumnModel[]> = {
  response_details_extended: widgetResponseDetailsExtendedColumns,
  alignment_response_detail: widgetAlignmentResponseDetailsColumns,
  response_details: widgetResponseDetailsColumns,
  alignment_shift: widgetAlignmentShiftColumns,
  mir_details: widgetMIRDetailsColumns,
};

interface ProcessorOptions {
  labels?: any;
  widgetId?: string;
  columnIndexOrder?: ColumnOrderType;
  columns?: ColumnModel[];
}

// Excel needs dates saved as a number so that it can format it as a date WITHOUT timezone shift.
function dateToExcelSerial(isoString: string): number {
  const [datePart] = isoString.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  // Days from Excel epoch (Dec 30, 1899) using UTC to avoid any TZ shift
  const date = Date.UTC(y, m - 1, d);
  const epoch = Date.UTC(1899, 11, 30);
  return Math.round((date - epoch) / 86_400_000);
}

const DATE_FIELDS = new Set(['latestAlignmentResponse', 'latestEngagement', 'latestResponse']);

export const isDateField = (field: string | undefined): boolean => {
  if (!field) {
    return false;
  }

  return DATE_FIELDS.has(field) || field.toLowerCase().includes('date');
};

export const formatExcelValue = (
  field: string | undefined,
  value: any,
  row?: any
): string | number => {
  if (['originalAggregates', 'children'].includes(field ?? '')) {
    return '';
  }

  switch (true) {
    case isDateField(field):
      return value && value !== 'N/A'
        ? dateToExcelSerial(formatAppDate(value, EXCEL_EXPORT_DATE_FORMAT))
        : (value as string);
    case Array.isArray(value):
      return (value as string[])
        .map((item) => item?.trim())
        .sort((a: string, b: string) => a.localeCompare(b))
        .join(', ');
    case field?.toLowerCase().includes('tags'): {
      if (!value) return '';

      const tags = Array.isArray(value)
        ? value
        : typeof value === 'string'
          ? value.split(',')
          : Object.values(value);

      return tags
        .map((tag) => String(tag).trim())
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .join(', ');
    }

    case ['sentimentLabel'].includes(field ?? ''):
      return (value as string) ?? 'Pending';
    case ['sentimentReason'].includes(field ?? ''): {
      if (row?.sentimentLabel === SentimentLabel.NOT_AVAILABLE) {
        return NotAvailableReason;
      }
      if (!value) {
        return PendingReason;
      }
      return String(value ?? '');
    }
    case field === 'activeStatus':
      if (value === true) {
        return 'Active';
      }

      if (value === false) {
        return 'Expired';
      }

      return '';
    case field === 'questionDetailsButton':
      return row?.questions?.length > 0 ? String(row.questions.length) : '';
    default:
      return value as string;
  }
};

// Updates all values in the data array to be properly formatted for Excel export
export const updateExcelDataFormats = (data: any[]): any[] => {
  return data.map((row) => {
    const updatedRow: { [key: string]: any } = {};
    Object.keys(row).forEach((key) => {
      updatedRow[key] = formatExcelValue(key, row[key]);
    });

    return updatedRow;
  });
};

const getColumnsByWidgetId = (widgetId: string) => {
  switch (widgetId) {
    case 'response_details':
      return GRID_COLUMNS.response_details;
    case 'response_details_extended':
      return GRID_COLUMNS.response_details_extended;
    case 'alignment_response_detail':
      return GRID_COLUMNS.alignment_response_detail;
    case 'alignment_shift':
      return GRID_COLUMNS.alignment_shift;
    case 'mir_detail':
      return GRID_COLUMNS.mir_details;
    default:
      return null;
  }
};
export function getOrderedColumnsForExport({
  columns,
  columnOrder,
}: {
  columns: ColumnModel[] | KwelloGridColumnModel[];
  columnOrder?: ColumnOrderType;
}) {
  return sortColumns({
    columns,
    columnIndexOrder: columnOrder,
  }).filter((col) => !NON_EXPORT_FIELDS.includes(col.field ?? col.cellType ?? ''));
}
function getColumnTitle(col: ColumnModel | KwelloGridColumnModel): string {
  if ('title' in col && col.title) {
    return col.title;
  }

  if ('text' in col && col.text) {
    return col.text;
  }

  return col.field ?? col.key ?? '';
}

const processStandardListData = (
  data: ListResponse,
  { widgetId, columns: propColumns }: ProcessorOptions = {}
) => {
  const columns = propColumns ?? getColumnsByWidgetId(widgetId as string) ?? [];

  const exportData: ExcelExportItem[] = [];

  if (data) {
    data.data.forEach((row: any) => {
      const newRow: ExcelExportItem = {};

      columns.forEach((col) => {
        if (!col.field && !col.key) {
          return;
        }

        const title = getColumnTitle(col);
        const field = col.field ?? col.key ?? '';

        newRow[title] = row[field];
      });

      exportData.push(newRow);
    });
  }

  return exportData;
};

export const getAlignmentProfileQuestionColumns = (
  data: ListResponse,
  { columnIndexOrder }: ProcessorOptions = {}
): ColumnModel[] => {
  const originalColumns = [...widgetAlignmentProfileQuestionColumns];

  const columns = sortColumns({ columns: originalColumns, columnIndexOrder });

  const questionsArray: QuestionType[] = data.data[0]?.questions
    ? Object.values(data.data[0]?.questions)
    : [];

  questionsArray.forEach((q: QuestionType) => {
    // @ts-expect-error title exsits as this is only used for kendo grids
    const exists = columns.find((c) => c.title === q.questionName);
    if (!exists) {
      columns.push({
        field: `Questions[${q.id}].AlignmentLabel`,
        title: q.questionName,
        sortable: false,
        headerClassName: 'k-column-title-wrap hide-header-menu',
        filterable: false,
        // @ts-expect-error this is only used for kendo grids
        width: '120px',
        cellType: 'alignmentLabel',
        cell: (props: GridCellProps) => {
          const propQuestion = props.dataItem.questions.find(
            (ques: QuestionType) => ques.questionName === q.questionName
          );

          return AlignmentTextCell(
            propQuestion?.alignmentScore,
            propQuestion?.alignmentChangeScore || 0,
            propQuestion?.alignmentColor1,
            propQuestion?.alignmentColor2,
            propQuestion?.alignmentLabel
          );
        },
      });
    }
  });
  // @ts-expect-error this is only used for kendo grids
  return [...columns, ...widgetAlignmentProfileQuestionEndColumns];
};

const processAlignmentProfileQuestion = (
  originalData: ListResponse,
  { columnIndexOrder }: ProcessorOptions = {}
) => {
  // Temporary data fudge until Bryntum is rolled out
  const data = Array.isArray(originalData.data[0]?.questions)
    ? originalData
    : {
        ...originalData,
        data: originalData.data.map((item) => ({
          ...item,
          questions: Object.values(item.questions || {}),
        })),
      };

  const columns = getAlignmentProfileQuestionColumns(data);

  const sortedColumns = sortColumns({ columns, columnIndexOrder });
  const exportData: ExcelExportItem[] = [];

  data.data.forEach((row: any) => {
    const newRow: ExcelExportItem = {};

    sortedColumns.forEach((col) => {
      if (!col.field || NON_EXPORT_FIELDS.includes(col.field)) {
        return;
      }

      if (col.field.includes('AlignmentLabel') || col.field.includes('alignmentQuestionId')) {
        return (newRow[col.field] = row.questions.find(
          // @ts-expect-error title exsits as this is only used for kendo grids
          (question: any) => question.questionName === col.title && !!question.alignmentScore
        )?.alignmentLabel as string);
      } else {
        return (newRow[col.field] = row[col.field ?? ''] as string);
      }
    });

    exportData.push(newRow);
  });

  return exportData;
};

const processFractionTable = (data: ListResponse, { labels }: ProcessorOptions = {}) => {
  const exportData: ExcelExportItem[] = [];
  data.data.forEach((row: any) => {
    exportData.push({
      [labels?.['value'] ? labels['value'] : 'Value']: formatExcelValue('string', row.name),
      [labels?.['count'] ? labels['count'] : 'Count']: formatExcelValue(
        'number',
        row.aggregates.Count
      ),
    });
  });

  return exportData;
};

const processProfileInteractionDetails = (data: ListResponse): any[] => {
  return data.data.map((row: any): any => ({
    ...row,
    aggregates: row.aggregates.reduce((acc: any, aggregate: any) => {
      acc[aggregate.name] = aggregate.value;
      return acc as Record<string, any>;
    }, {}),
  }));
};

const convertAggregatesArray = (aggregates: AggregateType[]): { [key: string]: number } => {
  const result: { [key: string]: number } = {};
  aggregates.forEach((aggregate) => {
    result[aggregate.name] = aggregate.value;
  });

  return result;
};

interface CoachTeamDetailChild {
  id: string;
  name: string;
  latestResponse: any;
  chartData: any;
  aggregates: Record<string, number>;
  originalAggregates: AggregateType[];
}

interface CoachTeamDetailItem {
  id: string;
  name: string;
  latestResponse: any;
  chartData: any;
  aggregates: Record<string, number>;
  originalAggregates: AggregateType[];
  children?: CoachTeamDetailChild[];
}

export const processCoachTeamDetailTable = (data: any): CoachTeamDetailItem[] => {
  const modifiedData: CoachTeamDetailItem[] = data.data.map((data: any) => {
    const aggregatesObject = convertAggregatesArray(data.aggregates);
    const baseItem: CoachTeamDetailItem = {
      id: data.id,
      name: data.name,
      latestResponse: data.latestResponse,
      chartData: data.chartData,
      aggregates: { ...aggregatesObject },
      originalAggregates: data.aggregates,
    };

    if (data.children && data.children.length > 0) {
      return {
        ...baseItem,
        children: data.children.map((child: any) => {
          const childAggregatesObject = convertAggregatesArray(child.aggregates);
          return {
            id: child.id,
            name: child.name,
            latestResponse: child.latestResponse,
            chartData: child.chartData,
            aggregates: { ...childAggregatesObject },
            originalAggregates: child.aggregates,
          };
        }),
      };
    }

    return baseItem;
  });

  return modifiedData;
};

export const excelProcessors: {
  [key: string]: (_data: ListResponse, options?: ProcessorOptions) => any;
} = {
  response_details_extended: processStandardListData,
  response_details: processStandardListData,
  alignment_response_detail: processStandardListData,
  alignment_profile_question: processAlignmentProfileQuestion,
  alignment_shift: processStandardListData,
  mir_detail: processStandardListData,
  disease_state: processFractionTable,
  treatment_table: processFractionTable,
  category_table: processFractionTable,
  keywords_table: processFractionTable,
  profile_count: processFractionTable,
  specialty_table: processFractionTable,
  provider_type_table: processFractionTable,
  geography_table: processFractionTable,
  profile_interaction_details: processProfileInteractionDetails,
  coach_detail_team_detail_totals: processCoachTeamDetailTable,
  coach_detail_team_detail_per_day: processCoachTeamDetailTable,
  coach_detail_team_detail_per_profile: processCoachTeamDetailTable,
  'all-responses': processStandardListData,
  'kwello-kendo-grid': processStandardListData,
  [WidgetType.KWELLO_BRYNTUM_GRID]: processStandardListData,
};

export const hasAsyncExport = [
  'interaction_details',
  'profile_interaction_details',
  'interaction_interaction_details',
  'tabbed_widget_interaction_details',
];

export const normalizeNumberPrecision = (n: number): number => {
  // if is integer do nothing
  if (Number(n) === n && n % 1 === 0) {
    return n;
  }

  return Number(n.toFixed(2));
};

export const getGeographyRegionColor = (
  count: number,
  min: number,
  max: number
): GeographyRegionColor => {
  const lightColor = getCssColor('--kendo-color-series-map-scale-start'); // light blue
  const darkColor = getCssColor('--kendo-color-series-map-scale-end'); // dark blue
  const emptyColor = getCssColor('--kendo-color-disabled'); // gray
  const ratio = Math.min(1, Math.max(0, (count - min) / (max - min || 1)));
  const adjustedRatio = Math.pow(ratio, 0.6);

  const r = Math.round(lightColor.r + (darkColor.r - lightColor.r) * adjustedRatio);
  const g = Math.round(lightColor.g + (darkColor.g - lightColor.g) * adjustedRatio);
  const b = Math.round(lightColor.b + (darkColor.b - lightColor.b) * adjustedRatio);
  const rgb =
    count === 0
      ? `rgb(${emptyColor.r}, ${emptyColor.g}, ${emptyColor.b})`
      : `rgb(${r}, ${g}, ${b})`;
  const hex = rgbToHex(rgb);
  const alpha = count === 0 ? 0.3 : 0.8; // Use 0.3 for empty regions, otherwise 0.8
  return { rgb, hex, alpha };
};

export function rgbToHex(rgbStr: string): string {
  // Match "rgb(r, g, b)" or "rgba(r, g, b, a)"
  const match = rgbStr.match(/^rgba?\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (!match) {
    throw new Error(`Invalid RGB(A) string: "${rgbStr}"`);
  }

  const [r, g, b] = match.slice(1, 4).map(Number);
  if ([r, g, b].some((v) => v < 0 || v > 255)) {
    throw new Error(`RGB values must be between 0–255: "${rgbStr}"`);
  }

  return [r, g, b]
    .map((val) => val.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}
